<template>
  <v-dialog :value="value" max-width="1200" scrollable>
    <v-card>
      <v-card-title>
        <h3>
          {{ this.translate("title") }} ({{ this.translate(this.target) }})
        </h3>
        <v-spacer></v-spacer>
        <v-btn icon @click="getZippedAttachments">
          <v-icon>mdi-reload</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row>
          <!-- Select Range -->
          <v-col>
            <DateInput
              v-model="export_range"
              :label="$t('zipped_reports_section.select_period')"
              :clearable="true"
              :range="true"
            />
          </v-col>
          <!-- Selected Client -->
          <v-col>
            <!-- Client -->
            <v-autocomplete
                flat
                :items="clients"
                :value="selectedClientLocal"
                :label="$t('billings.client')"
                class="mr-2 to-upper orange-input-field"
                item-value="id"
                item-text="name"
                @input="selectedClientLocal = $event"
                @click:append="selectedClientLocal = null"
                prepend-icon="mdi-filter"
                :search-input.sync="searchClient"
                return-object
                hide-no-data
                clearable
            ></v-autocomplete>
          </v-col>
        </v-row>
        
        <!-- Jobs Progress -->
        <v-row>
          <v-col cols="12">

            <v-container v-if="loadingData" class="d-flex justify-center align-center">
                <v-progress-circular
                indeterminate
                size="30"
                width="2"
                color="primary"
                ></v-progress-circular>
            </v-container>
            <v-data-table
              v-else
              :headers="jobHeaders"
              :items="allJobs"
              :items-per-page="5"
              class="elevation-1"
              :hide-default-footer="allJobs.length <= 5"
            >
              <!-- Job Type Column -->
              <template v-slot:item.job_type="{ item }">
                <div>
                  <div>{{ getJobTitle(item) }}</div>
                  <v-chip 
                    x-small 
                    outlined 
                    color="primary" 
                    v-if="item.metadata && item.metadata.date_label"
                    class="mt-1"
                  >
                    <v-icon x-small left>mdi-calendar-range</v-icon>
                    {{ item.metadata.date_label }}
                  </v-chip>
                </div>
              </template>

              <!-- Current Step Column -->
              <template v-slot:item.current_step="{ item }">
                <div class="text-caption">{{ item.current_step || 'Processing...' }}</div>
              </template>

              <!-- Progress Column -->
              <template v-slot:item.progress="{ item }">
                <div style="min-width: 150px;">
                  <v-progress-linear
                    :value="item.progress_percentage"
                    color="primary"
                    height="20"
                    rounded
                  >
                    <template v-slot:default="{ value }">
                      <strong style="font-size: 11px;">{{ Math.ceil(value) }}%</strong>
                    </template>
                  </v-progress-linear>
                </div>
              </template>

              <!-- Status Column -->
              <template v-slot:item.status="{ item }">
                <v-chip :color="getJobStatusColor(item.status)" small dark>
                  {{ item.status }}
                </v-chip>
              </template>

              <!-- Total Items Column -->
              <template v-slot:item.total_items="{ item }">
                <span>{{ item.total_items || 0 }}</span>/<span>{{ item.processed_items || 0 }}</span>/<span :class="item.failed_items > 0 ? 'error--text' :''">{{ item.failed_items || 0 }}</span>
              </template>

              <!-- Elapsed Time Column -->
              <template v-slot:item.elapsed_time="{ item }">
                <span>{{ formatElapsedTime(item.elapsed_time) }}</span>
              </template>

              <!-- Started At Column -->
              <template v-slot:item.started_at="{ item }">
                <span class="text-caption">{{ formatDateTime(item.started_at) }}</span>
              </template>

              <!-- Expanded Row for Error Messages -->
              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" v-if="item.error_message">
                  <v-alert type="error" dense class="my-2">
                    {{ item.error_message }}
                  </v-alert>
                </td>
              </template>
              <template v-slot:item.files_processed="{ item }">
                <div v-if="item?.metadata?.attachment && item?.metadata?.attachment?.public_url">
                    <v-icon right @click="download(item?.metadata?.attachment?.public_url)"> mdi-folder-zip-outline</v-icon>
                </div>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <span @click="close">
          <CloseButton />
        </span>
        <v-btn class="ma-2" @click="launchConfirmation" color="success">
          <v-icon class="mr-2">mdi-export</v-icon>
          {{ translate("export") }}</v-btn
        >
      </v-card-actions>
    </v-card>
    <confirmation-model
      v-if="confirmationDialog"
      :showDialog.sync="confirmationDialog"
      :openedForOperation.sync="confirmationDialogOperation"
      :text.sync="confirmationDialogConfirmationText"
      :trueText.sync="confirmationDialogTrueText"
      :falseText.sync="confirmationDialogFalseText"
      @buttonClicked="confirmationButtonClicked"
    ></confirmation-model>
  </v-dialog>
</template>
<script>
import CloseButton from "@/components/Buttons/CloseButton.vue";
import { API_BASE_URL } from "@/config";
import axios from "@/plugins/axios";
import ConfirmationModel from "@/components/ConfirmationModel.vue";
import DateInput from "@/components/DateInput.vue";
import _ from "lodash";

export default {
  name: "ZippedReports",
  components: { CloseButton, ConfirmationModel, DateInput },
  emits: ["input"],
  refs: ["menu"],
  props: {
    target: {
      type: String,
      default: "orders",
    },
    value: {
      type: Boolean,
      default: false,
    },
    selectedArticles: [],
    selectedStatus: null,
    selectedClient: null,
  },
  data() {
    return {
      searchClient: null,
      selectedClientLocal: null,
      clients: [],
      attachments: [],
      allJobs: [],
      jobHeaders: [
        { 
          text: this.$t('zipped_reports_section.job_type'), 
          value: 'job_type', 
          sortable: false,
          width: '180px'
        },
        { 
          text: this.$t('zipped_reports_section.current_step'), 
          value: 'current_step', 
          sortable: false,
          width: '200px'
        },
        { 
          text: this.$t('zipped_reports_section.total_items') + "/" + this.$t('zipped_reports_section.processed_items')  + "/" +  this.$t('zipped_reports_section.failed_items'), 
          value: 'total_items', 
          sortable: true,
          align: 'center'
        },
        { 
          text: this.$t('zipped_reports_section.elapsed_time'), 
          value: 'elapsed_time', 
          sortable: true,
          align: 'center'
        },
        { 
          text: this.$t('zipped_reports_section.progress'), 
          value: 'progress', 
          sortable: false,
          width: '150px',
          align: 'center'
        },
        { 
          text: this.$t('zipped_reports_section.status'), 
          value: 'status', 
          sortable: false,
          width: '100px',
          align: 'center'
        },
        { 
          text: this.$t('zipped_reports_section.files_processed'), 
          value: 'files_processed', 
          sortable: true,
          width: '150px'
        }
      ],
      axiosOptions: {
        Authorization: "Bearer " + this.$store.state.AccessToken,
      },
      type: null,
      year: null,
      export_range: null,
      yearPicker: false,
      menu: null,
      partialExport: false,
      confirmationDialog: false,
      confirmationDialogOperation: null,
      confirmationDialogConfirmationText: null,
      confirmationDialogTrueText: null,
      confirmationDialogFalseText: null,
      toBeDeleted: null,
      billingStatuses: [],
      batchSize: 30,
      loadingData: false,
      pollingInterval: null
    };
  },
  watch: {
    value(val) {
      if (!val) {
        this.year = null;
        this.export_range = null;
        // this.partialExport = true;
        this.stopPolling();
      } else {
        this.startPolling();
      }
      if(this.selectedClient){
        this.clients = [this.selectedClient];
        this.selectedClientLocal = this.selectedClient;
      } else {
        this.clients = [];
        this.selectedClientLocal = null;
      }

      this.getZippedAttachments();
      this.getallJobs();
    },
    searchClient: _.debounce(function () {
      if (!this.searchClient) {
        return;
      }

      let searchTerm = this.searchClient.toString();
      searchTerm = searchTerm.trim();
      if (this.searchClient && this.searchClient.length > 0) {
        if (this.selectedClient && this.selectedClient.name == this.searchClient) {
          return;
        }

        this.clients = [];
        this.isLoadingSearchClientInput = true;
        axios
          .get(API_BASE_URL + "/clients/search?search=" + searchTerm, {
            headers: this.header,
          })
          .then(({ data }) => {
            data.data.forEach((element) => {
              element.clientname =
                element.name + " " + (element.first_name ?? "");
            });
            this.clients = data.data;
          })
          .catch(function (error) {
            console.log("an error occured " + error);
          })
          .finally(() => {
            this.isLoadingSearchClientInput = true;
          });
      }
    }, 400),
  },
  async mounted() {
    this.billingStatuses = await this.getCodes(
      "order_status",
      "billings.statuses"
    );
  },
  beforeDestroy() {
    this.stopPolling();
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    getallJobs() {
      axios
        .get(API_BASE_URL + "/job-progress", {
          headers: this.axiosOptions,
        })
        .then(({data}) => {
          this.allJobs = data?.data?.data || [];
          
          // If there are no active jobs, stop polling
          if (this.allJobs.length === 0 && this.pollingInterval) {
            this.stopPolling();
          }
        })
        .catch((error) => {
          console.error("Error fetching active jobs:", error);
        });
    },
    startPolling() {
      // Poll every 3 seconds
      this.pollingInterval = setInterval(() => {
        this.getallJobs();
      }, 3000);
    },
    stopPolling() {
      if (this.pollingInterval) {
        clearInterval(this.pollingInterval);
        this.pollingInterval = null;
      }
    },
    formatElapsedTime(seconds) {
      if (!seconds) return '0s';
      
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      if (hours > 0) {
        return `${hours}h ${minutes}m ${secs}s`;
      } else if (minutes > 0) {
        return `${minutes}m ${secs}s`;
      } else {
        return `${secs}s`;
      }
    },
    formatDateTime(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        return date.toLocaleString();
      } catch (e) {
        return dateString;
      }
    },
    getJobStatusColor(status) {
      const statusColors = {
        'processing': 'primary',
        'completed': 'success',
        'failed': 'error',
        'pending': 'warning'
      };
      return statusColors[status] || 'grey';
    },
    getJobTitle(job) {
      // Format job type to be more readable
      const jobType = job.job_type || 'Processing';
      const formattedType = jobType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      // Add date label if available in metadata
      if (job.metadata && job.metadata.date_label) {
        return formattedType;
      }
      
      return formattedType;
    },
    translate(key, args = {}) {
      return this.$t("zipped_reports_section." + key, args);
    },
    getZippedAttachments() {
      return new Promise((resolve, reject) => {
        this.loadingData = true;
        let url = API_BASE_URL + "/zipped_reports?target=" + this.target;
        if (this.type) url += "&type=" + this.type;

        axios
          .get(url, { headers: this.axiosOptions })
          .finally(() => {
            this.loadingData = false;
          })
          .then((response) => {
            this.attachments = response.data.data;
            resolve(this.attachments);
          })
          .catch((error) => {
            reject(error);
          });
      });
    },
    download(url) {
      window.location.assign(url);
      this.$toast.success(this.translate("download_starting"));
    },
    launchConfirmation() {
      if (!this.export_range) {
        return this.$toast.error(this.translate("year_not_selected"));
      }

    //   if (this.batchSize > 200 || this.batchSize < 10) {
    //     return this.$toast.error(this.translate("batch_size_error"));
    //   }

      this.confirmationDialog = true;
      this.confirmationDialogOperation = "export";
      this.confirmationDialogConfirmationText =
        this.translate("confirmation_text");
      this.confirmationDialogTrueText = this.translate(
        "confirmation_true_text"
      );
      this.confirmationDialogFalseText = this.translate(
        "confirmation_false_text"
      );
    },

    launchDeleteConfirmation(deleteId) {
      this.confirmationDialog = true;
      this.confirmationDialogOperation = "delete";
      this.confirmationDialogConfirmationText = this.translate(
        "delete_confirmation_text"
      );
      this.confirmationDialogTrueText = this.translate(
        "confirmation_true_text"
      );
      this.confirmationDialogFalseText = this.translate(
        "confirmation_false_text"
      );
      this.toBeDeleted = deleteId;
    },
    confirmationButtonClicked(action) {
      if (!action) return;

      if (this.confirmationDialogOperation === "export") {
        this.exportAttachmentHandler();
      }

      if (this.confirmationDialogOperation === "delete") {
        this.deleteAttachmentHandler();
      }
    },
    deleteAttachmentHandler() {
      axios
        .delete(API_BASE_URL + "/attachments/" + this.toBeDeleted, {
          headers: this.axiosOptions,
        })
        .finally(() => {})
        .then(() => {
          this.$toast.success(this.translate("delete_success"));
          this.attachments = this.attachments.filter(
            (item) => item.id !== this.toBeDeleted
          );
        })
        .catch(() => {
          this.$toast.error(this.translate("delete_failed"));
        });
    },
    exportAttachmentHandler(sendEmailLink = false) {
      // this.$toast.success(this.translate('export_queued'));
      let payload = {
        // date: this.year,
        date_range: this.export_range,
        target: this.target,
        partial_export: this.partialExport,
        client_id: this.selectedClientLocal?.id || null,
        status_id: this.selectedStatus?.id || null,
        batch_size: this.batchSize,
        ids: this.selectedArticles,
      };
      if (sendEmailLink) {
        payload["send_email"] = true;
        payload["send_email_to"] = this.currentUser.email;
      }
      axios
        .post(API_BASE_URL + "/zipped_reports", payload, {
          headers: this.axiosOptions,
        })
        .finally(() => {})
        .then(() => {
          if (sendEmailLink) {
            this.$toast.success(
              this.translate(
                "we_will_send_link_to_your_email_when_process_gets_completed"
              )
            );
          } else {
            this.$toast.success(this.translate("export_queued"));
          }
          // Start polling for jobs after export is queued
          this.startPolling();
          this.getallJobs();
          // Don't close the dialog so user can see progress
          // this.close();
        })
        .catch(() => {
          this.$toast.error(this.translate("export_failed"));
        });
    },
  },
  computed: {
    selectedItems() {
      if (
        this.listingSelectionsFlatten &&
        this.listingSelectionsFlatten.length > 0
      ) {
        return this.listingSelectionsFlatten
          .map((element) => {
            return element.id;
          })
          .join(",");
      }
      return "";
    },
    listingSelectionsFlatten() {
      return this.$store.state.listingSelectionsFlatten;
    },
  },
};
</script>
